/**
 * zzz-hover-cards — custom (store-owned) section.
 *
 * Below the theme's `md` breakpoint (769px) the cards behave as an accordion:
 * each card's title is a toggle button and the body text expands/collapses on
 * tap. At `md` and above on a hover-capable device the text is revealed on
 * hover by CSS alone, and this component only clears any open state left over
 * from the mobile layout. Touch devices above `md` (tablets) keep the toggle.
 *
 * Progressive enhancement: until this script runs, `data-accordion-ready` is
 * absent and the CSS leaves every card's text visible on mobile — the same
 * behaviour the section had before the accordion was added.
 *
 * See assets/zzz-hover-cards.css and sections/zzz-hover-cards.liquid.
 */
class HoverCards extends HTMLElement {
  connectedCallback() {
    this.toggles = Array.from(this.querySelectorAll('.hover-card__toggle'));
    if (this.toggles.length === 0) return;

    this.clickHandler = this.handleClick.bind(this);
    this.breakpointChangeHandler = this.handleBreakpointChange.bind(this);

    this.addEventListener('click', this.clickHandler);
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    this.isAccordion = !theme.mediaMatches.md;

    // Signals to the CSS that the collapsed state is now safe to apply.
    this.setAttribute('data-accordion-ready', '');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
  }

  handleClick(evt) {
    const toggle = evt.target.closest('.hover-card__toggle');
    if (!toggle || !this.contains(toggle)) return;

    this.setCardExpanded(toggle, toggle.getAttribute('aria-expanded') !== 'true');
  }

  /**
   * Collapses any open cards when moving up to the hover layout, so a card left
   * open on mobile doesn't stay pinned open once hover takes over.
   */
  handleBreakpointChange() {
    const isAccordion = !theme.mediaMatches.md;
    if (isAccordion === this.isAccordion) return;
    this.isAccordion = isAccordion;

    if (!isAccordion && theme.device.hasHover) {
      this.toggles.forEach((toggle) => this.setCardExpanded(toggle, false));
    }
  }

  // eslint-disable-next-line class-methods-use-this
  setCardExpanded(toggle, expanded) {
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.closest('.hover-card').classList.toggle('is-open', expanded);
  }
}

customElements.define('hover-cards', HoverCards);
